import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Download, Search, ChevronDown, ChevronRight, Plus, Pencil, RefreshCw, SlidersHorizontal, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { inventoryApi } from "./inventoryApi";
import { formatDateTime } from "./inventoryConstants";

const ACTION_STYLE = {
  created: { pill: "bg-green-100 text-green-700", chip: "bg-green-50", icon: Plus, color: "text-green-600" },
  updated: { pill: "bg-blue-100 text-blue-700", chip: "bg-blue-50", icon: Pencil, color: "text-blue-600" },
  status_changed: { pill: "bg-indigo-100 text-indigo-700", chip: "bg-indigo-50", icon: RefreshCw, color: "text-indigo-600" },
  adjustment: { pill: "bg-amber-100 text-amber-700", chip: "bg-amber-50", icon: SlidersHorizontal, color: "text-amber-600" },
  quantity_adjusted: { pill: "bg-amber-100 text-amber-700", chip: "bg-amber-50", icon: SlidersHorizontal, color: "text-amber-600" },
};
const DEFAULT_STYLE = { pill: "bg-slate-100 text-slate-600", chip: "bg-slate-100", icon: FileText, color: "text-slate-500" };

const RECORD_LABELS = {
  part: "Part", vendor: "Vendor", location: "Location", parts_request: "Parts Request",
  purchase_order: "Purchase Order", receipt: "Receipt", core: "Core", salvaged_part: "Salvaged Part", tire: "Tire",
};

const exportCsv = (rows) => {
  const cols = ["timestamp", "record_type", "action", "user", "record_id"];
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = "audit-trail.csv"; a.click();
  URL.revokeObjectURL(url);
};

const AuditRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const style = ACTION_STYLE[entry.action] || DEFAULT_STYLE;
  const Icon = style.icon;
  const hasDetail = (entry.changes && entry.changes.length > 0) || (entry.snapshot && Object.keys(entry.snapshot).length > 0);
  return (
    <div className="border-b border-slate-50 last:border-0" data-testid="audit-row">
      <button onClick={() => hasDetail && setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${style.chip}`}>
          <Icon className={`w-4 h-4 ${style.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-slate-900 truncate">{RECORD_LABELS[entry.record_type] || entry.record_type}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${style.pill}`}>{(entry.action || "").replace(/_/g, " ")}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
            {formatDateTime(entry.timestamp)} · {entry.user}{entry.changes?.length > 0 ? ` · ${entry.changes.length} field(s)` : ""}
          </div>
        </div>
        {hasDetail && (open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />)}
      </button>
      {open && hasDetail && (
        <div className="px-[60px] pb-3 space-y-2">
          {entry.changes?.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              {entry.changes.map((c, i) => (
                <div key={i} className="text-xs flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-700">{c.field}:</span>
                  <span className="text-red-600 line-through">{String(c.previous ?? "—")}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-green-700">{String(c.new ?? "—")}</span>
                </div>
              ))}
            </div>
          )}
          {entry.snapshot && Object.keys(entry.snapshot).length > 0 && (
            <div className="text-xs text-slate-500">
              {Object.entries(entry.snapshot).map(([k, v]) => <span key={k} className="mr-3"><span className="font-medium">{k}:</span> {String(v)}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AuditSection = () => {
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({ record_types: [], actions: [], users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [recordType, setRecordType] = useState("");
  const [action, setAction] = useState("");
  const [user, setUser] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => { inventoryApi.getAuditMeta().then(setMeta).catch(() => {}); }, []);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (recordType) params.record_type = recordType;
      if (action) params.action = action;
      if (user) params.user = user;
      if (search) params.search = search;
      setEntries(await inventoryApi.getAudit(params));
    } catch { toast.error("Failed to load audit trail"); }
    setLoading(false);
  }, [recordType, action, user, search]);

  useEffect(() => { const t = setTimeout(fetchAudit, 250); return () => clearTimeout(t); }, [fetchAudit]);

  return (
    <div className="space-y-4" data-testid="audit-section">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <ShieldCheck className="w-4 h-4 text-blue-500" />
        <span>Immutable audit trail — every create, update, and status change is recorded ({meta.total} total)</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search type, action, user, id..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search" />
        </div>
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={recordType} onChange={(e) => setRecordType(e.target.value)} data-testid="audit-type-filter">
          <option value="">All Records</option>
          {meta.record_types.map((r) => <option key={r} value={r}>{RECORD_LABELS[r] || r}</option>)}
        </select>
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action-filter">
          <option value="">All Actions</option>
          {meta.actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
        </select>
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white max-w-[160px]" value={user} onChange={(e) => setUser(e.target.value)} data-testid="audit-user-filter">
          <option value="">All Users</option>
          {meta.users.map((u) => <option key={u} value={u}>{u.length > 20 ? `${u.slice(0, 18)}…` : u}</option>)}
        </select>
        {entries.length > 0 && (
          <button onClick={() => exportCsv(entries)} className="h-9 px-3 rounded-md border border-slate-200 text-sm text-blue-600 inline-flex items-center gap-1 bg-white" data-testid="audit-export">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        )}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : entries.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No audit entries</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-3 py-2 text-xs text-slate-400 border-b border-slate-100">{entries.length} entries</div>
          {entries.map((e) => <AuditRow key={e.id} entry={e} />)}
        </div>
      )}
    </div>
  );
};
